import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import Chip from '@mui/material/Chip';
import SchoolIcon from '@mui/icons-material/School';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import CategoryIcon from '@mui/icons-material/Category';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * 流程类型选择对话框组件 - 左右分栏布局版
 */
const FlowTypeDialog = ({ open, onClose, currentFlow, flowTypes, onSelect, accentColor = '#4D8BFF' }) => {
  const { t, i18n } = useTranslation();
  const isZhCN = !i18n.language || i18n.language === 'zh-CN';
  const safeFlowTypes = Array.isArray(flowTypes) ? flowTypes : [];
  const [selectedFlow, setSelectedFlow] = useState(currentFlow);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // 多语言辅助函数：获取流程显示名
  const getFlowDisplayName = (flow) => {
    if (!isZhCN && flow.displayNameEN) return flow.displayNameEN;
    return flow.displayName;
  };

  // 多语言辅助函数：获取流程描述
  const getFlowDescription = (flow) => {
    if (!isZhCN && flow.descriptionEN) return flow.descriptionEN;
    return flow.description;
  };

  // 多语言辅助函数：获取分类显示名
  const getCategoryDisplayName = (category) => {
    if (!isZhCN) {
      // 从流程数据中查找该分类的英文名
      const flowWithEN = safeFlowTypes.find(f => f.category === category && f.categoryEN);
      if (flowWithEN) return flowWithEN.categoryEN;
    }
    return category;
  };

  const parseWorkflowPhases = (value) => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'string') return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const compactText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const getPhaseTitle = (phase, index) => compactText(
    phase?.name || phase?.title || phase?.label || phase?.displayName || phase?.id || `阶段${index + 1}`
  );

  const getPhaseGoal = (phase) => compactText(
    phase?.goal || phase?.objective || phase?.objectives || phase?.learningGoal || phase?.description || phase?.desc
  );

  const buildFlowInsightCards = (flow) => {
    const phases = parseWorkflowPhases(flow?.workflowPhases);
    const phaseNames = phases.map(getPhaseTitle).filter(Boolean);
    const evaluationCommands = Array.isArray(flow?.evaluationCommands) ? flow.evaluationCommands.filter(Boolean) : [];
    const firstPhaseGoal = phases.map(getPhaseGoal).find(Boolean);
    const cards = [];

    if (phases.length > 0 || flow?.stageCount > 0) {
      cards.push({
        label: '阶段安排',
        text: phaseNames.length > 0
          ? `${phases.length || flow.stageCount} 个阶段：${phaseNames.slice(0, 6).join(' → ')}${phaseNames.length > 6 ? '…' : ''}`
          : `${flow.stageCount} 个阶段，阶段名称暂未维护。`
      });
    }
    if (firstPhaseGoal) {
      cards.push({
        label: '进入后先做什么',
        text: `先从“${phaseNames[0] || '第一阶段'}”开始：${firstPhaseGoal}`
      });
    }
    if (evaluationCommands.length > 0) {
      cards.push({
        label: '评价与反馈',
        text: evaluationCommands.slice(0, 5).join('、')
      });
    }
    if (flow?.marketVersion) {
      cards.push({
        label: '资源版本',
        text: `当前使用市集版本 ${flow.marketVersion}`
      });
    }
    return cards;
  };

  useEffect(() => {
    if (currentFlow) {
      setSelectedFlow(currentFlow);
    } else if (safeFlowTypes.length > 0) {
      const bronzeStandardFlow = safeFlowTypes.find(flow => flow.name === 'new_user_first_experience');
      if (bronzeStandardFlow) {
        setSelectedFlow('new_user_first_experience');
      }
    }
    
  }, [safeFlowTypes, currentFlow]);

  // 搜索过滤处理（支持名称、课程、分类与流程介绍）
  const filteredFlows = useMemo(() => {
    if (!searchTerm) return safeFlowTypes;
    const term = searchTerm.toLowerCase();
    return safeFlowTypes.filter(flow => {
      const courseNames = (flow.courseScopes || []).map(scope => scope.courseName || '').join(' ');
      return (
      (flow.displayName || '').toLowerCase().includes(term) || 
      (flow.displayNameEN || '').toLowerCase().includes(term) ||
      (flow.category || '').toLowerCase().includes(term) ||
      (flow.categoryEN || '').toLowerCase().includes(term) ||
      (flow.description || '').toLowerCase().includes(term) ||
      courseNames.toLowerCase().includes(term)
      );
    });
  }, [safeFlowTypes, searchTerm]);

  const courseGroups = useMemo(() => {
    const groups = new Map();
    filteredFlows
      .filter(flow => flow.scopeType === 'course' && Array.isArray(flow.courseScopes) && flow.courseScopes.length > 0)
      .forEach(flow => {
        flow.courseScopes.forEach(scope => {
          const courseId = scope.courseId || scope.courseName;
          if (!courseId) return;
          if (!groups.has(courseId)) {
            groups.set(courseId, {
              id: courseId,
              name: scope.courseName || t('flow_unknown_course'),
              flows: []
            });
          }
          groups.get(courseId).flows.push(flow);
        });
      });

    return [...groups.values()]
      .map(group => ({
        ...group,
        flows: [...new Map(group.flows.map(flow => [flow.name, flow])).values()]
          .sort((a, b) => `${a.category || ''}${a.displayName || ''}`.localeCompare(`${b.category || ''}${b.displayName || ''}`))
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredFlows, t]);

  // 「教师推荐」分区已下线：推荐入口统一到「我的课程」；此处仅保留非课程绑定流程。
  const otherFlows = useMemo(
    () => filteredFlows
      .filter(flow => flow.scopeType !== 'course')
      .sort((a, b) => `${a.category || ''}${a.displayName || ''}`.localeCompare(`${b.category || ''}${b.displayName || ''}`)),
    [filteredFlows]
  );

  const otherFlowCategories = useMemo(() => {
    const categories = new Map();
    otherFlows.forEach(flow => {
      const category = flow.category || t('flow_uncategorized');
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category).push(flow);
    });
    return [...categories.entries()].sort(([categoryA], [categoryB]) => {
      const aIsBronzeStandard = categoryA.includes('青铜工程四钻标准流程');
      const bIsBronzeStandard = categoryB.includes('青铜工程四钻标准流程');
      if (aIsBronzeStandard && !bIsBronzeStandard) return -1;
      if (!aIsBronzeStandard && bIsBronzeStandard) return 1;
      return categoryA.localeCompare(categoryB);
    });
  }, [otherFlows, t]);

  // 课程流程优先展开；只有没有课程流程时，其他可用流程默认展开。
  useEffect(() => {
    const expanded = {};
    courseGroups.forEach(group => {
      expanded[`course-${group.id}`] = true;
    });
    if (searchTerm || courseGroups.length === 0) {
      expanded.other = true;
    }
    if (searchTerm) {
      otherFlowCategories.forEach(([category]) => {
        expanded[`other-category-${category}`] = true;
      });
    }
    if (courseGroups.length === 0) {
      otherFlowCategories.forEach(([category]) => {
        if (category.includes('青铜工程四钻标准流程')) {
          expanded[`other-category-${category}`] = true;
        }
      });
    }
    setExpandedSections(expanded);
  }, [courseGroups, otherFlowCategories, searchTerm]);

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };
  const selectedFlowInfo = safeFlowTypes.find(f => f.name === selectedFlow);

  const handleFlowDoubleClick = (flowName) => {
    setSelectedFlow(flowName);
    onSelect(flowName);
  };

  const activeColor = accentColor || '#4D8BFF';
  const bronzeColor = activeColor;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="flow-type-dialog" // 添加类名以便全局样式控制
      PaperProps={{
        className: 'workbench-dialog-paper',
        style: { '--workbench-accent': activeColor },
        sx: {
          backgroundColor: '#1e1e1e',
          color: '#e7e7e7',
          borderRadius: '10px',
          backgroundImage: 'none',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.38)',
          border: '1px solid #3e3e42',
          height: "calc(100vh - 120px)", // 直接设置高度为视口高度减去底部空间
          maxHeight: "900px", // 设置最大高度上限
          width: 'min(95vw, 1200px)', // 限制最大宽度
          margin: "32px auto", // 确保垂直居中且有外边距
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      {/* 标题栏 */}
      <DialogTitle sx={{ 
        padding: "16px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: '1px solid #3e3e42',
        borderTop: `2px solid ${activeColor}`,
        background: '#202124',
        backdropFilter: 'none',
        flexShrink: 0,
        zIndex: 10
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CategoryIcon sx={{ color: activeColor }} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.5px' }}>
            {t('flow_select_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: '#a6afb8',
            '&:hover': { color: '#fff', backgroundColor: '#303238' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      {/* 内容区域：左右分栏 */}
      <DialogContent sx={{ 
        padding: 0, 
        display: 'flex', 
        flexDirection: 'row', // 关键：改为横向布局
        height: '100%',
        overflow: 'hidden'
      }}>
        
        {/* 左侧：流程列表导航 */}
        <Box sx={{ 
          width: '340px',
          minWidth: '340px',
          borderRight: '1px solid #3e3e42',
          display: 'flex',
          flexDirection: 'column',
          background: '#202124',
        }}>
          
          {/* 搜索框区域 */}
          <Box sx={{ p: 2, borderBottom: '1px solid #343438' }}>
            <TextField
              fullWidth
              placeholder={t('flow_search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#a6afb8' }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: '#292a2d',
                  color: '#e7e7e7',
                  fontSize: '14px',
                  '& fieldset': { border: '1px solid #3e3e42' },
                  '&:hover fieldset': { borderColor: '#5b6068' },
                  '&.Mui-focused fieldset': { borderColor: activeColor },
                }
              }}
            />
          </Box>

          {/* 滚动列表区域 */}
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '2px' },
          }}>
          
            {courseGroups.length > 0 && (
              <Typography variant="caption" sx={{ color: activeColor, fontWeight: 700, letterSpacing: '0.5px', px: 1, mb: 1, display: 'block' }}>
                {t('flow_my_course_flows')}
              </Typography>
            )}
            {courseGroups.map((group) => {
              const sectionKey = `course-${group.id}`;
              const isExpanded = !!expandedSections[sectionKey];
              return (
                <Box key={sectionKey} sx={{ mb: 1.5 }}>
                  <FlowSectionHeader
                    title={group.name}
                    count={group.flows.length}
                    expanded={isExpanded}
                    onClick={() => toggleSection(sectionKey)}
                    icon={<SchoolIcon sx={{ fontSize: 18 }} />}
                    color={activeColor}
                  />
                  {isExpanded && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                      {group.flows.map((flow) => (
                        <FlowListItem
                          key={`${sectionKey}-${flow.name}`}
                          flow={flow}
                          displayName={getFlowDisplayName(flow)}
                          metaText={flow.category ? getCategoryDisplayName(flow.category) : t('flow_uncategorized')}
                          selected={selectedFlow === flow.name}
                          onClick={() => setSelectedFlow(flow.name)}
                          onDoubleClick={() => handleFlowDoubleClick(flow.name)}
                          themeColor={activeColor}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}

            {otherFlows.length > 0 && (
              <Box sx={{ mt: courseGroups.length > 0 ? 2 : 0 }}>
                <FlowSectionHeader
                  title={t('flow_other_available')}
                  count={otherFlows.length}
                  expanded={!!expandedSections.other}
                  onClick={() => toggleSection('other')}
                  icon={<CategoryIcon sx={{ fontSize: 18 }} />}
                  color="#a6afb8"
                />
                {expandedSections.other && otherFlowCategories.map(([category, flows]) => {
                  const categoryKey = `other-category-${category}`;
                  const categoryExpanded = !!expandedSections[categoryKey];
                  return (
                    <Box key={category} sx={{ mt: 1.25, pl: 1 }}>
                      <FlowSectionHeader
                        title={getCategoryDisplayName(category)}
                        count={flows.length}
                        expanded={categoryExpanded}
                        onClick={() => toggleSection(categoryKey)}
                        icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                        color="#c3c9d0"
                        nested
                      />
                      {categoryExpanded && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5, pl: 1 }}>
                          {flows.map((flow) => (
                            <FlowListItem
                              key={`other-${flow.name}`}
                              flow={flow}
                              displayName={getFlowDisplayName(flow)}
                              metaText={flow.scopeType === 'organization' ? t('flow_org_available') : t('flow_public_available')}
                              selected={selectedFlow === flow.name}
                              onClick={() => setSelectedFlow(flow.name)}
                              onDoubleClick={() => handleFlowDoubleClick(flow.name)}
                              themeColor={activeColor}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {courseGroups.length === 0 && otherFlows.length === 0 && searchTerm && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(255,255,255,0.4)' }}>
                    <Typography variant="body2">{t('flow_no_match')}</Typography>
                </Box>
            )}
          </Box>
        </Box>

        {/* 右侧：详情预览 & 操作区 */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          background: '#252526'
        }}>
          {selectedFlowInfo ? (
            <>
              {/* 详情内容区 - 可滚动 */}
              <Box sx={{ 
                flex: 1, 
                padding: "40px", 
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '3px' },
              }}>
                {/* 大图标 */}
                {(() => {
                  const category = selectedFlowInfo.category || '';
                  const isBeginnerCategory = category.includes('新手') || category.includes('bronze') || category.includes('入门');
                  const detailColor = isBeginnerCategory ? bronzeColor : activeColor;
                  return (
                    <Box sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#292a2d',
                      border: `1px solid ${detailColor}`,
                      mb: 3,
                      boxShadow: 'none'
                    }}>
                      {isBeginnerCategory 
                        ? <SchoolIcon sx={{ fontSize: 32, color: detailColor }} /> 
                        : <RocketLaunchIcon sx={{ fontSize: 32, color: detailColor }} />
                      }
                    </Box>
                  );
                })()}

                {/* 标题和标签 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#e7e7e7', letterSpacing: '0' }}>
                    {getFlowDisplayName(selectedFlowInfo)}
                  </Typography>
                  {selectedFlowInfo.name === currentFlow && (
                    <Chip 
                      label={t('flow_current_running')} 
                      size="small" 
                      sx={{ 
                        backgroundColor: `${activeColor}1f`, 
                        color: activeColor,
                        border: `1px solid ${activeColor}66`,
                        fontWeight: 600
                      }} 
                    />
                  )}
                </Box>
                {selectedFlowInfo.courseScopes?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                    {selectedFlowInfo.courseScopes.map((scope) => (
                      <Chip
                        key={scope.courseId || scope.courseName}
                        label={scope.courseName || t('flow_unknown_course')}
                        size="small"
                        variant="outlined"
                        sx={{ color: '#c3c9d0', borderColor: '#565b63', backgroundColor: '#292a2d' }}
                      />
                    ))}
                  </Box>
                )}

                {/* 描述文字 */}
                <Typography variant="body1" sx={{ 
                  color: '#c3c9d0', 
                  lineHeight: 1.8, 
                  fontSize: '15px',
                  maxWidth: '700px'
                }}>
                  {getFlowDescription(selectedFlowInfo) || t('flow_no_desc')}
                </Typography>

                <Box sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: '8px',
                  background: '#292a2d',
                  border: `1px solid ${activeColor}66`,
                  maxWidth: '760px',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start'
                }}>
                  <InfoOutlinedIcon sx={{ color: activeColor, fontSize: 20, mt: '2px' }} />
                  <Typography variant="body2" sx={{ color: '#c3c9d0', lineHeight: 1.75 }}>
                    训练流程是老师设计的工程剧本，通常由多个阶段组成；每个阶段包含微场景和问题。新手建议优先体验“造物初体验”。若老师布置了任务，请优先选择老师指定或课程相关流程。
                  </Typography>
                </Box>

                {(() => {
                  const cards = buildFlowInsightCards(selectedFlowInfo);
                  if (cards.length === 0) {
                    return (
                      <Box sx={{
                        mt: 4,
                        p: 2,
                        borderRadius: '8px',
                        background: '#292a2d',
                        border: '1px solid #56514a',
                        maxWidth: '760px'
                      }}>
                        <Typography variant="body2" sx={{ color: '#c3c9d0', lineHeight: 1.7 }}>
                          该流程暂未维护阶段结构、评价方式或资源版本信息。请以进入训练后的任务阶段提示为准。
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{
                      mt: 4,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: cards.length >= 3 ? 'repeat(3, 1fr)' : `repeat(${cards.length}, minmax(0, 1fr))` },
                      gap: 2,
                      width: '100%',
                      maxWidth: '920px'
                    }}>
                      {cards.map(({ label, text }) => (
                        <Box key={label} sx={{
                          p: 2,
                          borderRadius: '8px',
                          background: '#292a2d',
                          border: '1px solid #3e3e42'
                        }}>
                          <Typography variant="caption" sx={{ color: activeColor, fontWeight: 700, display: 'block', mb: 1 }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#c3c9d0', lineHeight: 1.7 }}>
                            {text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}

              </Box>

              {/* 底部固定操作栏 */}
              <Box sx={{ 
                padding: "20px 40px",
                borderTop: '1px solid #3e3e42',
                background: '#202124',
                backdropFilter: 'none',
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                zIndex: 10
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#858d96' }}>
                  <TouchAppIcon fontSize="small" />
                  <Typography variant="caption">
                    {t('flow_double_click_hint')}
                  </Typography>
                </Box>

                {(() => {
                  const category = selectedFlowInfo.category || '';
                  const isBeginnerCategory = category.includes('新手') || category.includes('bronze') || category.includes('入门');
                  const btnColor = isBeginnerCategory ? bronzeColor : activeColor;
                  const btnHoverColor = activeColor;
                  return (
                    <Button 
                      variant="contained"
                      onClick={() => onSelect(selectedFlow)}
                      endIcon={<KeyboardDoubleArrowRightIcon />}
                      sx={{
                        backgroundColor: btnColor,
                        color: isBeginnerCategory ? '#0f172a' : '#fff',
                        padding: "10px 32px",
                        fontSize: "15px",
                        fontWeight: 700,
                        borderRadius: '6px',
                        boxShadow: 'none',
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: btnHoverColor,
                          transform: 'none',
                          boxShadow: 'none',
                        }
                      }}
                    >
                      {selectedFlow === currentFlow ? t('flow_restart') : t('flow_confirm')}
                    </Button>
                  );
                })()}
              </Box>
            </>
          ) : (
            // Empty State
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.2)'
            }}>
              <AutoAwesomeIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="body1">{t('flow_select_hint')}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const FlowSectionHeader = ({ title, count, expanded, onClick, icon, color, nested = false }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      px: nested ? 1.25 : 1,
      py: nested ? 0.9 : 0.8,
      borderRadius: '6px',
      backgroundColor: nested ? '#292a2d' : 'transparent',
      borderLeft: nested ? `2px solid ${color}` : '2px solid transparent',
      transition: 'background-color 0.16s ease',
      '&:hover': { backgroundColor: nested ? '#303238' : '#292a2d' }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography variant="caption" sx={{ color, fontWeight: nested ? 700 : 700, fontSize: nested ? '12px' : undefined, letterSpacing: '0.3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: '#858d96', flexShrink: 0 }}>
        {count}
      </Typography>
    </Box>
    {expanded ? <ExpandLessIcon sx={{ fontSize: 18, color: '#858d96' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: '#858d96' }} />}
  </Box>
);

// 子组件：侧边栏列表项
const FlowListItem = ({ flow, displayName, metaText, selected, onClick, onDoubleClick, themeColor, recommendationBadge = '' }) => {
  return (
    <Box
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      sx={{
        position: 'relative',
        padding: '10px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'background-color 0.16s ease, border-color 0.16s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: selected ? `${themeColor}1f` : 'transparent',
        // 选中时的左侧指示条
        '&::before': selected ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '15%',
          bottom: '15%',
          width: '3px',
          backgroundColor: themeColor,
          borderRadius: '0 2px 2px 0',
          boxShadow: 'none'
        } : {},
        '&:hover': {
          backgroundColor: selected ? `${themeColor}2a` : '#292a2d',
          transform: 'none'
        }
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ 
          fontWeight: selected ? 600 : 400,
          color: selected ? '#fff' : '#c3c9d0',
          fontSize: '13px',
          letterSpacing: '0.2px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {displayName || flow.displayName}
        </Typography>
        {metaText && (
          <Typography variant="caption" sx={{
            color: '#858d96',
            fontSize: '11px',
            display: 'block',
            mt: 0.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {metaText}
          </Typography>
        )}
      </Box>
      {recommendationBadge && (
        <Typography variant="caption" sx={{ color: themeColor, fontWeight: 700, fontSize: '11px', flexShrink: 0 }}>
          {recommendationBadge}
        </Typography>
      )}
    </Box>
  );
};

export default FlowTypeDialog;